import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { getStaticText } from '../../../../utils/constants';
import getStaticTextDiagnose from '../../constants';
import { getBenchmarksLabel, getDemographicsLabel, manipulateHeatmapTitle } from '../../../../utils/functions';
import {
    ExcelFilterRows,
    encodeFillCell,
    getExcelFileName,
    getIeScoreCell,
    isNSizeTitle,
    writeColoredExcel,
} from '../excelUtils';

const TABLE_ID = 'ieDeepDiveExcelTable';

function Excel({
    variant,
    headerToUse,
    dataToUse,
    header,
    dataToUseOhi,
    ohiScoreToUse,
    practiceCategories,
    mapToUse,
    selectedTab,
    filters,
    selectedDemographics,
    defaultSettings,
    crossTabDemo,
    crossTabLabel,
    orgHealth,
    singleQuestion,
    setSelectedDownload,
}) {
    const {
        lang = '1033',
        ohid = '',
        engagement = '',
        apply_employee_exp_benchmark = false,
        quartileColors = [],
    } = defaultSettings;
    const staticTextDiagnose = getStaticTextDiagnose(lang);
    const { INT_TO_LEAVE_DEMO, ENPS_DEMO } = staticTextDiagnose;
    const staticText = getStaticText(lang);
    const { SITE_TEXT, NON_PROFIT_SURVEY, NEG_IND_QUES = [] } = staticText;
    const {
        NEGATIVE_PRACTICES = [],
        N_SIZE,
        OHI_SCORE_TEXT,
        END_ORG_TXT,
        INT_TO_LEAVE_TXT,
        DEMO_SELECTED_OPTIONS,
        BENCHMARK_FILTERS,
        BENCHMARK_NA,
        QUESTION_HEAD,
        OVERALL_SCORED,
        HEALTHY,
        UNHEALTHY,
        EXRTREMELY_LIKELY,
        AGREE_OR_HIGHER,
    } = SITE_TEXT;
    const isNonProfit = engagement === NON_PROFIT_SURVEY;
    const { demographics = [], benchmarks = [], ex_benchmarks = [] } = filters[0] || {};
    const selectedGroup = demographics.find(({ code }) => code === selectedDemographics) || {};
    const selectedLabel = crossTabLabel || selectedGroup.label || '';
    const demoFilterLabels = getDemographicsLabel(demographics);
    const benchLabel = getBenchmarksLabel(benchmarks);
    const exBenchLabel = apply_employee_exp_benchmark ? getBenchmarksLabel(ex_benchmarks) || '' : BENCHMARK_NA;
    const filePrefix =
        variant === 'orgHealth' ? 'Employee_Experience_Deep_Dive_OrgHealth' : 'Employee_Experience_Deep_Dive';

    const getExport = () => {
        setSelectedDownload('');
        return writeColoredExcel(TABLE_ID, getExcelFileName(filePrefix, defaultSettings), {
            preventDateCoercion: true,
        });
    };

    useEffect(() => {
        getExport();
        // eslint-disable-next-line
    }, []);

    const shouldExcludePractice = prName =>
        (prName === INT_TO_LEAVE_TXT && crossTabDemo === INT_TO_LEAVE_DEMO) ||
        (prName === END_ORG_TXT && crossTabDemo === ENPS_DEMO) ||
        (prName === 'eNPS' && crossTabDemo === ENPS_DEMO);

    const renderDeepDiveRows = () => {
        const { children = [], children_ohi_slug = [] } = mapToUse[selectedTab] || {};
        const ohiRows = [];
        children_ohi_slug.forEach(elem => {
            const row = (dataToUseOhi || []).find(({ slug = '' }) => slug === elem || elem === 'n');
            if (!row) return;
            const { items: subItems = [], title: rowTitle = '' } = row;
            const title = manipulateHeatmapTitle(rowTitle);
            const isN = isNSizeTitle(title) || elem === 'n';
            ohiRows.push(
                <tr key={`ohi-${title}`}>
                    <td>bold#{title}</td>
                    {subItems.map(({ score, quartile }, index) => (
                        <td key={`${title}-${index}`}>
                            {getIeScoreCell({
                                score,
                                quartile,
                                isN,
                                applyBenchmark: true,
                                quartileColors,
                                forceQuartile: true,
                            })}
                        </td>
                    ))}
                </tr>
            );
        });

        const eeRows = [];
        (dataToUse || []).forEach((subItems, parentI) => {
            const title = header[parentI];
            if (!children.includes(title)) return;
            const isNegativeTrait = NEGATIVE_PRACTICES.includes(title);
            eeRows.push(
                <tr key={`ee-${title}-${parentI}`}>
                    <td>bold#{title}</td>
                    {(subItems || []).map(({ score, quartile = '' }, index) => (
                        <td key={`${title}-${index}`}>
                            {getIeScoreCell({
                                score,
                                quartile,
                                isNegativeTrait,
                                applyBenchmark: apply_employee_exp_benchmark,
                                quartileColors,
                                isNonProfit,
                                ohid,
                            })}
                        </td>
                    ))}
                </tr>
            );
        });

        return (
            <>
                {ohiRows}
                {eeRows}
            </>
        );
    };

    const renderEnpsRows = () => {
        const ohiRows = (ohiScoreToUse || []).slice(0, 2).map((list, i) => {
            const title = !i ? 'n' : OHI_SCORE_TEXT;
            return (
                <tr key={`enps-ohi-${title}`}>
                    <td>bold#{title}</td>
                    {(list || []).map(({ score, quartile }, index) => (
                        <td key={`${title}-${index}`}>
                            {getIeScoreCell({
                                score,
                                quartile,
                                isN: !i,
                                applyBenchmark: true,
                                quartileColors,
                                forceQuartile: true,
                            })}
                        </td>
                    ))}
                </tr>
            );
        });

        const eeRows = [];
        practiceCategories.forEach(({ children: child = [] }) => {
            child
                .filter(prName => !shouldExcludePractice(prName))
                .forEach(elem => {
                    const index = header.indexOf(elem);
                    if (index === -1 || header[index] === N_SIZE || !dataToUse[index]) return;
                    const title = manipulateHeatmapTitle(header[index], lang);
                    const isNegativeTrait = NEGATIVE_PRACTICES.includes(title);
                    const subItems = dataToUse[index] || [];
                    eeRows.push(
                        <tr key={`enps-ee-${title}-${index}`}>
                            <td>bold#{title}</td>
                            {subItems.map(({ score: innerScore, quartile = '' }, cellIndex) => {
                                const hideZero =
                                    (crossTabDemo === INT_TO_LEAVE_DEMO && parseInt(innerScore, 10) === 0) ||
                                    (crossTabDemo === ENPS_DEMO && parseInt(innerScore, 10) === 0);
                                const scoreToUse = hideZero ? '-' : innerScore;
                                return (
                                    <td key={`${title}-${cellIndex}`}>
                                        {getIeScoreCell({
                                            score: scoreToUse,
                                            quartile,
                                            isNegativeTrait,
                                            applyBenchmark: apply_employee_exp_benchmark,
                                            quartileColors,
                                            isNonProfit,
                                            ohid,
                                        })}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                });
        });

        return (
            <>
                {ohiRows}
                {eeRows}
            </>
        );
    };

    const renderOrgHealthRows = () => {
        const { records = {} } = orgHealth || {};
        const { qbyq = {} } = singleQuestion || {};
        const { records: qbyqRecords = {} } = qbyq || {};
        return Object.keys(records).map((key, i) => {
            const { corelation, healthy, unhealthy } = records[key] || {};
            const objToUse = key === 'enps' ? qbyqRecords.endorsing_organization_to_others : qbyqRecords[key];
            const { meta_scores, score = '', quartile = '' } = objToUse || {};
            const { qtext: question = '' } = (meta_scores && meta_scores[0]) || {};
            const negativeTrait = NEG_IND_QUES.includes(key);
            const scoreCell = getIeScoreCell({
                score,
                quartile,
                isNegativeTrait: negativeTrait,
                applyBenchmark: apply_employee_exp_benchmark,
                quartileColors,
                isNonProfit,
                ohid,
            });
            const responseText = i === 1 ? `"9" or "10" (${EXRTREMELY_LIKELY})` : `"4" ${AGREE_OR_HIGHER}`;
            const formatted = num => `${num}`.toString().replace(/^0/, '');
            return (
                <tr key={key}>
                    <td>bold#{question}</td>
                    <td>{scoreCell}</td>
                    <td>#{responseText}</td>
                    <td>#{`rxy=${formatted(corelation)}`}</td>
                    <td>{encodeFillCell('#ffffff', '#000000', formatted(unhealthy))}</td>
                    <td>{encodeFillCell('#ffffff', '#000000', formatted(healthy))}</td>
                </tr>
            );
        });
    };

    const renderHeader = () => {
        if (variant === 'orgHealth') {
            return (
                <tr>
                    <th>bold#{QUESTION_HEAD}#center</th>
                    <th>bold#{OVERALL_SCORED}#center</th>
                    <th>bold#Respondents#center</th>
                    <th>bold#rxy#center</th>
                    <th>bold#{UNHEALTHY}#center</th>
                    <th>bold#{HEALTHY}#center</th>
                </tr>
            );
        }
        return (
            <tr>
                <th>{crossTabLabel ? `bold#${crossTabLabel}` : ''}</th>
                {headerToUse.map((val, i) => (
                    <th key={`${val}-${i}`}>bold#{manipulateHeatmapTitle(val, lang)}#center</th>
                ))}
            </tr>
        );
    };

    const renderTableBody = () => {
        if (variant === 'orgHealth') return renderOrgHealthRows();
        if (variant === 'enps') return renderEnpsRows();
        return renderDeepDiveRows();
    };

    return (
        <div>
            <table id={TABLE_ID} className="hideTable">
                <ExcelFilterRows
                    selectedLabel={selectedLabel}
                    demoFilterLabels={demoFilterLabels}
                    benchLabel={benchLabel}
                    exBenchLabel={exBenchLabel}
                    staticText={{ DEMO_SELECTED_OPTIONS, BENCHMARK_FILTERS }}
                />
                <tbody>
                    {renderHeader()}
                    {renderTableBody()}
                </tbody>
            </table>
        </div>
    );
}

Excel.propTypes = {
    variant: PropTypes.string,
    headerToUse: PropTypes.array,
    dataToUse: PropTypes.array,
    header: PropTypes.array,
    dataToUseOhi: PropTypes.array,
    ohiScoreToUse: PropTypes.array,
    practiceCategories: PropTypes.array,
    mapToUse: PropTypes.array,
    selectedTab: PropTypes.number,
    filters: PropTypes.array.isRequired,
    selectedDemographics: PropTypes.string,
    defaultSettings: PropTypes.object.isRequired,
    crossTabDemo: PropTypes.string,
    crossTabLabel: PropTypes.string,
    orgHealth: PropTypes.object,
    singleQuestion: PropTypes.object,
    setSelectedDownload: PropTypes.func.isRequired,
};

Excel.defaultProps = {
    variant: 'deepDive',
    headerToUse: [],
    dataToUse: [],
    header: [],
    dataToUseOhi: [],
    ohiScoreToUse: [],
    practiceCategories: [],
    mapToUse: [],
    selectedTab: 0,
    selectedDemographics: '',
    crossTabDemo: '',
    crossTabLabel: '',
    orgHealth: {},
    singleQuestion: {},
};

export default Excel;
